import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

const DUTCH_CITY_COORDS = {
  'amsterdam': { lat: 52.3676, lng: 4.9041 },
  'rotterdam': { lat: 51.9225, lng: 4.4792 },
  'den haag': { lat: 52.0705, lng: 4.3007 },
  'utrecht': { lat: 52.0907, lng: 5.1214 },
  'eindhoven': { lat: 51.4416, lng: 5.4697 },
  'groningen': { lat: 53.2194, lng: 6.5665 },
  'tilburg': { lat: 51.5555, lng: 5.0913 },
  'almere': { lat: 52.3508, lng: 5.2647 },
  'breda': { lat: 51.5719, lng: 4.7683 },
  'nijmegen': { lat: 51.8426, lng: 5.8527 },
  'arnhem': { lat: 51.9851, lng: 5.8987 },
  'haarlem': { lat: 52.3874, lng: 4.6462 },
  'leiden': { lat: 52.1601, lng: 4.4970 },
  'delft': { lat: 52.0116, lng: 4.3571 },
  'maastricht': { lat: 50.8514, lng: 5.6910 },
  'amersfoort': { lat: 52.1561, lng: 5.3878 },
  'dordrecht': { lat: 51.8133, lng: 4.6901 },
  'zoetermeer': { lat: 52.0575, lng: 4.4931 },
  'zwolle': { lat: 52.5168, lng: 6.0830 },
  'deventer': { lat: 52.2554, lng: 6.1600 },
  'leeuwarden': { lat: 53.2012, lng: 5.7999 },
  'enschede': { lat: 52.2215, lng: 6.8937 },
  'apeldoorn': { lat: 52.2112, lng: 5.9699 },
  'hilversum': { lat: 52.2292, lng: 5.1669 },
  'den bosch': { lat: 51.6978, lng: 5.3037 },
  "'s-hertogenbosch": { lat: 51.6978, lng: 5.3037 },
};

const ListingMap = ({ city, neighborhood }) => {
  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const geocode = async () => {
      setLoading(true);
      
      // Try Nominatim geocoding with neighborhood + city
      const searchQuery = neighborhood 
        ? `${neighborhood}, ${city}, Netherlands` 
        : `${city}, Netherlands`;
      
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&countrycodes=nl&limit=1`,
          { headers: { 'Accept-Language': 'nl' } }
        );
        const data = await response.json();
        
        if (data && data.length > 0) {
          setCoords({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
          setLoading(false);
          return;
        }
      } catch (e) {
        // Nominatim failed, try fallback
      }

      // Fallback: try city-only search
      if (neighborhood) {
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(city + ', Netherlands')}&countrycodes=nl&limit=1`,
            { headers: { 'Accept-Language': 'nl' } }
          );
          const data = await response.json();
          if (data && data.length > 0) {
            setCoords({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
            setLoading(false);
            return;
          }
        } catch (e) {
          // Also failed
        }
      }

      // Final fallback: local lookup
      const cityLower = (city || '').toLowerCase();
      const fallback = DUTCH_CITY_COORDS[cityLower] || { lat: 52.1326, lng: 5.2913 }; // Center of NL
      setCoords(fallback);
      setLoading(false);
    };

    if (city) {
      geocode();
    }
  }, [city, neighborhood]);

  if (loading) {
    return (
      <div className="aspect-video rounded-xl overflow-hidden bg-[#F7F7F7] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#2E7D32] animate-spin" />
      </div>
    );
  }

  if (!coords) return null;

  // Build OSM embed URL with marker and proper zoom
  const zoom = neighborhood ? 15 : 13;
  const delta = neighborhood ? 0.008 : 0.03;
  const bbox = `${coords.lng - delta},${coords.lat - delta},${coords.lng + delta},${coords.lat + delta}`;
  const markerParam = `&marker=${coords.lat},${coords.lng}`;

  return (
    <div className="aspect-video rounded-xl overflow-hidden bg-[#E5E5E5]">
      <iframe
        title="Locatie"
        width="100%"
        height="100%"
        frameBorder="0"
        src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik${markerParam}`}
      />
    </div>
  );
};

export default ListingMap;
